import { Box, Flex, Text } from "@chakra-ui/react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import React, { useEffect, useRef, useState } from "react";
import { disableScroll, enableScroll } from "utils";

const sourceMp4 = "https://cdn.sanity.io/files/pwmfmi47/production/991b79696e6a90e465084408686f43cadceac9ba.mov";
const sourceWebm = "https://cdn.sanity.io/files/pwmfmi47/production/095106805932ea736e3bfc2795b7fe0c5b5197ab.webm";

type AgentsPreloaderProps = {
  eyebrow: string;
  title: string;
  onComplete: () => void;
};

export function AgentsPreloader({ eyebrow, title, onComplete }: AgentsPreloaderProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isTenSecondsBeforeEnd, setIsTenSecondsBeforeEnd] = useState(false);

  const handlePlay = (event: React.SyntheticEvent) => {
    setIsPlaying(true);
    const video = event.currentTarget as HTMLVideoElement;
    if (video) {
      video.playbackRate = 1.5;
    }
  };

  const handleEnded = () => {
    setIsComplete(true);
  };

  const handleTimeUpdate = (event: React.SyntheticEvent) => {
    const video = event.currentTarget as HTMLVideoElement;
    if (video?.duration && !isTenSecondsBeforeEnd) {
      const timeRemaining = video.duration - video.currentTime;
      if (timeRemaining <= 10) {
        setIsTenSecondsBeforeEnd(true);
      }
    }
  };

  const handleLoadedMetadata = (event: React.SyntheticEvent) => {
    const video = event.currentTarget as HTMLVideoElement;
    if (video) {
      video.playbackRate = 1.5;
    }
  };

  useEffect(() => {
    if (isPlaying) {
      disableScroll();
      // OnPlay();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isComplete) {
      enableScroll();
      onComplete();
    }
  }, [isComplete, onComplete]);

  useEffect(() => {
    if (isTenSecondsBeforeEnd) {
      onComplete();
    }
  }, [isTenSecondsBeforeEnd, onComplete]);

  useGSAP(
    () => {
      // Set initial state: below and invisible for both texts
      gsap.set([titleRef.current, headingRef.current], {
        opacity: 0,
        y: 32,
        scale: 0.98,
      });

      const timeline = gsap.timeline();

      // Reveal: animate title first, then heading with delay
      timeline
        .to([titleRef.current, headingRef.current], {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          delay: 1,
          ease: "power3.out",
          stagger: 0.1,
        })

        // Exit: slide up further and fade out (after delay)
        .to(
          [titleRef.current, headingRef.current],
          {
            opacity: 0,
            y: -32,
            duration: 0.6,
            ease: "power3.in",
            stagger: 0.1,
          },
          "+=1.2",
        );
    },
    { scope: boxRef },
  );

  if (isComplete) {
    return null;
  }

  return (
    <Box pos="relative">
      <Box
        autoPlay
        muted
        playsInline
        as="video"
        preload="metadata"
        display={isComplete ? "none" : "block"}
        pos="fixed"
        inset={0}
        zIndex={3}
        width="100vw"
        height="100vh"
        pointerEvents="none"
        onPlay={handlePlay}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      >
        <source src={sourceMp4} type="video/mp4;codecs=hvc1" />
        {sourceWebm && <source src={sourceWebm} type="video/webm" />}
      </Box>

      <Box
        ref={boxRef}
        aria-hidden="true"
        pos="fixed"
        top="50%"
        left="50%"
        w="100%"
        transform="translate(-50%, -50%)"
        zIndex={3}
        pointerEvents="none"
      >
        <Flex w="100%" flexDir="column" align="center" textAlign="center" gap="big" maxW="1100px" mx="auto">
          <Box ref={titleRef}>
            <Text textStyle="body3" fontWeight="semibold">
              {eyebrow}
            </Text>
          </Box>
          <Box ref={headingRef}>
            <Text as="h1" textStyle="display2" textAlign="center">
              {title}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
